import { memo, useMemo } from 'react';
import { useCurrentUser, useNavigationHelper } from 'FrontRoyalAngular';
import { getCohort } from 'Users';
import { ProgramTypeConfigs } from 'Program';
import { SidebarBoxKey } from 'AdmissionsGuidance';
import { useTranslation } from 'react-i18next';
import { BasicMobileBox } from '../../BasicMobileBox';
import { BasicDesktopBox } from '../../BasicDesktopBox';
import { type ButtonConfig } from '../../StudentDashboardSidebar.types';
import { LogoBox } from '../../LogoBox';

function NotJoiningProgramCanReapplyBoxComponent({ mobile }: { mobile?: boolean }) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'notJoiningProgramCanReapplyBox' });
    const { loadRoute } = useNavigationHelper();
    const currentUser = useCurrentUser();
    const cohort = useMemo(() => currentUser && getCohort(currentUser), [currentUser]);
    const programTitle = useMemo(() => {
        if (!cohort) return null;
        const config = ProgramTypeConfigs[cohort.programType];
        return config.programBoxTitle?.(cohort) ?? config.fullTitle(cohort);
    }, [cohort]);

    const buttons = useMemo(
        () => [
            {
                id: 'button_complete_application',
                text: t('completeApplicationButton'),
                onPress: () => {
                    loadRoute('/settings/application');
                },
            },
        ],
        [t, loadRoute],
    );

    if (!programTitle) return null;

    return mobile ? (
        <NotJoiningProgramCanReapplyBoxMobilePresentation programTitle={programTitle} buttons={buttons} />
    ) : (
        <NotJoiningProgramCanReapplyBoxPresentation programTitle={programTitle} buttons={buttons} />
    );
}

function NotJoiningProgramCanReapplyBoxPresentation({
    programTitle,
    buttons,
}: {
    programTitle: string;
    buttons: ButtonConfig[];
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'notJoiningProgramCanReapplyBox' });
    return (
        <LogoBox title={programTitle} boxKey={SidebarBoxKey.not_joining_program_can_reapply} buttons={buttons}>
            {t('content')}
        </LogoBox>
    );
}

function NotJoiningProgramCanReapplyBoxMobilePresentation({
    programTitle,
    buttons,
}: {
    programTitle: string;
    buttons: ButtonConfig[];
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'notJoiningProgramCanReapplyBox' });
    return (
        <BasicMobileBox
            heading={programTitle}
            boxKey={SidebarBoxKey.not_joining_program_can_reapply}
            ctaText={t('mobileButton')!}
            expandedContent={
                <BasicDesktopBox
                    boxKey={SidebarBoxKey.not_joining_program_can_reapply}
                    headerText={programTitle}
                    buttons={buttons}
                >
                    {t('content')}
                </BasicDesktopBox>
            }
        />
    );
}

export const NotJoiningProgramCanReapplyBox = memo(NotJoiningProgramCanReapplyBoxComponent);
